import { getToken } from './keycloak'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

class ApiError extends Error {
  constructor(message, status, body) {
    super(message)
    this.status = status
    this.body = body
  }
}

async function request(path, { method = 'GET', body, headers = {} } = {}) {
  const token = getToken()
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  let data = null
  const text = await res.text()
  if (text) { try { data = JSON.parse(text) } catch { data = text } }

  if (!res.ok) {
    const message = data?.error || data?.message || `HTTP ${res.status}`
    throw new ApiError(message, res.status, data)
  }
  return data
}

export const api = {
  get: (path) => request(path),
  post: (path, body, headers) => request(path, { method: 'POST', body, headers }),
  put: (path, body, headers) => request(path, { method: 'PUT', body, headers }),
  patch: (path, body, headers) => request(path, { method: 'PATCH', body, headers }),
  delete: (path) => request(path, { method: 'DELETE' }),
}

export { ApiError }

export const vaultsApi = {
  list: () => api.get('/keystores').then((r) => r.keystores ?? []),
  create: (payload) => api.post('/keystores', payload),
  update: (uuid, payload, keystoreVersion) => api.patch(
    `/keystores/${uuid}`,
    payload,
    { 'If-Match': `"${keystoreVersion}"` },
  ),
  rotatePassword: (uuid, payload, keystoreVersion) => api.post(
    `/keystores/${uuid}/rotate-password`,
    payload,
    { 'If-Match': `"${keystoreVersion}"` },
  ),
  delete: (uuid) => api.delete(`/keystores/${uuid}`),
}

export const entriesApi = {
  list: (vaultUuid) => api.get(`/entries/${vaultUuid}`).then((r) => r.entries ?? []),
  create: (vaultUuid, payload) => api.post(`/entries/${vaultUuid}`, payload),
  update: (vaultUuid, entryUuid, payload, entryVersion) => api.patch(
    `/entries/${vaultUuid}/${entryUuid}`,
    payload,
    { 'If-Match': `"${entryVersion}"` },
  ),
  move: (vaultUuid, entryUuid, payload, entryVersion) => api.post(
    `/entries/${vaultUuid}/${entryUuid}/move`,
    payload,
    { 'If-Match': `"${entryVersion}"` },
  ),
  delete: (vaultUuid, entryUuid) => api.delete(`/entries/${vaultUuid}/${entryUuid}`),
}

export const attachmentsApi = {
  listVaultMetadata: (vaultUuid) => api.get(`/attachments/${vaultUuid}`).then((r) => r.attachments ?? []),
  list: (vaultUuid, entryUuid) => api.get(`/attachments/${vaultUuid}/${entryUuid}`).then((r) => r.attachments ?? []),
  get: (vaultUuid, entryUuid, attachmentUuid) => api.get(`/attachments/${vaultUuid}/${entryUuid}/${attachmentUuid}`)
    .then((r) => r.attachments?.[0] ?? null),
  create: (vaultUuid, entryUuid, payload) => api.post(`/attachments/${vaultUuid}/${entryUuid}`, payload),
  update: (vaultUuid, entryUuid, attachmentUuid, payload, attachmentVersion) => api.patch(
    `/attachments/${vaultUuid}/${entryUuid}/${attachmentUuid}`,
    payload,
    { 'If-Match': `"${attachmentVersion}"` },
  ),
  rotatePassword: (vaultUuid, entryUuid, attachmentUuid, payload, attachmentVersion) => api.post(
    `/attachments/${vaultUuid}/${entryUuid}/${attachmentUuid}/rotate-password`,
    payload,
    { 'If-Match': `"${attachmentVersion}"` },
  ),
  delete: (vaultUuid, entryUuid, attachmentUuid) => api.delete(`/attachments/${vaultUuid}/${entryUuid}/${attachmentUuid}`),
}


export const settingsApi = {
  get: () => api.get('/user/settings'),
  put: (settings) => api.put('/user/settings', settings),
  patch: (partialSettings) => api.patch('/user/settings', partialSettings),
  reset: () => api.delete('/user/settings'),
}

export const userApi = {
  pingConnection: () => api.post('/userConnection'),
}
